import { classnames } from 'Utils/classnames/classnames.utils';
import React, { memo } from 'react';
import { constants } from '@clubmed/utils';
import type { CSSProperties } from 'react';
import type { RatioDisplay } from 'Components/frameworks/image/Image';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import type { Props as DynamicLinkProps } from 'Components/frameworks/dynamic-link/DynamicLink';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Image } from 'Components/frameworks/image/Image';
import { ImageSlideShow } from 'Components/frameworks/image-slideshow/ImageSlideShow';
import { Tag } from 'Components/frameworks/tag/Tag';
const { OPENING_STATUS } = constants;
type Picture = {
  alt?: string;
  onClick?: () => void;
  src: string;
};
type Tags = Array<{
  className?: string;
  dataTestId?: string;
  iconName?: string;
  label: string;
  style?: CSSProperties;
}>;
export type Props = {
  bottomTags?: Tags;
  className?: string;
  closedLabel?: string;
  hasLazyLoading?: boolean;
  isLinkDisabled?: boolean;
  isSliderDisabled?: boolean;
  isTagsDisabled?: boolean;
  link?: DynamicLinkProps;
  nextLabel?: string;
  openingStatus?: string;
  pictures: Array<Picture>;
  previousLabel?: string;
  ratioDisplay?: RatioDisplay;
  topTags?: Tags;
};
export const ProductThumbnail = memo<Props>(function ProductThumbnail({
  bottomTags = [],
  className,
  closedLabel,
  hasLazyLoading,
  isLinkDisabled,
  isSliderDisabled,
  isTagsDisabled = false,
  link,
  nextLabel,
  openingStatus,
  pictures = [],
  previousLabel,
  ratioDisplay = 1 / 3,
  topTags = [],
}: Props) {
  const TagName = isLinkDisabled ? 'div' : DynamicLink;
  const defaultPicture: Picture = pictures[0] ?? { src: '', alt: '' };
  return (
    <TagName
      className={classnames('relative block', className)}
      {...(isLinkDisabled ? null : link)}
    >
      {!isSliderDisabled && pictures.length > 1 && openingStatus !== OPENING_STATUS.CLOSED ? (
        <ImageSlideShow
          hasLazyLoading
          itemClassName="isolate overflow-hidden rounded-12 will-change-transform"
          media={pictures}
          nextLabel={nextLabel}
          previousLabel={previousLabel}
          ratio={ratioUtils.XL}
          ratioDisplay={ratioDisplay}
        />
      ) : (
        <Image
          altMsg={defaultPicture.alt}
          imageUrl={defaultPicture.src}
          key={defaultPicture.src}
          ratioKey={ratioUtils.XL}
          ratioDisplay={ratioDisplay}
          hasLazyLoading={hasLazyLoading}
        />
      )}
      {!!topTags?.length && !isTagsDisabled && (
        <div
          className="absolute top-0 flex w-full justify-end gap-5 p-10"
          data-testid="ProductThumbnail-topTags"
        >
          {topTags.map((tag) => (
            <Tag
              className={tag.className}
              iconName={tag.iconName}
              data-testid={tag.dataTestId}
              key={tag.dataTestId || tag.label}
              style={tag.style}
            >
              {tag.label}
            </Tag>
          ))}
        </div>
      )}
      {openingStatus === OPENING_STATUS.CLOSED && (
        <div
          className="absolute inset-0 z-1 inline-flex items-center justify-center overflow-hidden rounded-12 bg-black/60 text-20 font-bold uppercase text-white"
          data-testid="ProductThumbnail-closed"
        >
          {closedLabel}
        </div>
      )}
      {!!bottomTags?.length && !isTagsDisabled && (
        <div
          className="absolute bottom-0 flex w-full justify-start gap-5 p-10"
          data-testid="ProductThumbnail-bottomTags"
        >
          {bottomTags.map((tag) => (
            <Tag
              className={tag.className}
              iconName={tag.iconName}
              data-testid={tag.dataTestId}
              key={tag.label}
              style={tag.style}
            >
              {tag.label}
            </Tag>
          ))}
        </div>
      )}
    </TagName>
  );
});
